'use client';

/* eslint-disable consistent-return */
import { useMemo } from 'react';
import { useCollator } from 'react-aria';
import type { Key , ListState } from 'react-stately';
import type { DropTarget, KeyboardDelegate } from '@react-types/shared';
import { intersects } from '../get-visible-nodes';
import type { GetSize, LayoutInfo, Rect } from '../types';
import { buildStackLayout } from './build-stack-layout';
import { stackKeyboardDelegate } from './stack-keyboard-delegate';

type UseStackLayoutProps<T> = {
  visibleRect: Rect;
  state: ListState<T>;
  getSize: GetSize<T>;
  orientation?: 'vertical';
  sectionGap?: number;
  itemGap?: number;
  paddingTop?: number;
  paddingBottom?: number;
};

export function useStackLayout<T>({
  visibleRect,
  state,
  getSize,
  sectionGap = 0,
  itemGap = 0,
  paddingTop = 0,
  paddingBottom = 0,
}: UseStackLayoutProps<T>) {
  const collator = useCollator({ usage: 'search', sensitivity: 'base' });

  /**
   * Build a tree of layout nodes based upon the collection and
   * the measured sizes of items.
   */

  const {
    nodes: layout,
    totalSize,
    layoutInfo,
  } = useMemo(() => {
    return buildStackLayout({
      collection: state.collection,
      getSize,
      sectionGap,
      paddingTop,
      paddingBottom,
      itemGap,
    });
  }, [
    state.collection,
    getSize,
    sectionGap,
    paddingTop,
    paddingBottom,
    itemGap,
  ]);

  /**
   * Keyboard delegate for the list, used by react-aria
   * useListBox hook to determine which keys to focus
   * when the user presses navigation keys.
   * Based upon: https://github.com/adobe/react-spectrum/blob/109ea066ed6d953abd2ee2cd7f8051823ae7cf52/packages/%40react-aria/selection/src/ListKeyboardDelegate.ts#L29
   */

  const keyboardDelegate = useMemo<KeyboardDelegate>(() => {
    const { getFirstKey, getLastKey, getKeyAbove, getKeyBelow } =
      stackKeyboardDelegate({
        collection: state.collection,
        disabledKeys: state.disabledKeys,
      });

    function getKeyPageAbove(key: Key) {
      let layout = layoutInfo.get(key);

      if (layout) {
        const pageY = Math.max(
          0,
          layout.rect.y - layout.rect.height - visibleRect.height
        );
        while (layout && layout.rect.y > pageY) {
          // @ts-expect-error FIXME(strictNullChecks)
          const keyAbove = getKeyAbove(layout.key);
          // @ts-expect-error FIXME(strictNullChecks)
          layout = layoutInfo.get(keyAbove);
        }
        if (layout) {
          return layout.key;
        }
      }

      // @ts-expect-error FIXME(strictNullChecks)
      return getFirstKey();
    }

    function getKeyPageBelow(key: Key) {
      let layout = layoutInfo.get(key);

      if (layout) {
        const pageY = Math.min(
          totalSize,
          layout.rect.y - layout.rect.height + visibleRect.height
        );
        while (layout && layout.rect.y < pageY) {
          // @ts-expect-error FIXME(strictNullChecks)
          const keyBelow = getKeyBelow(layout.key);
          // @ts-expect-error FIXME(strictNullChecks)
          layout = layoutInfo.get(keyBelow);
        }

        if (layout) {
          return layout.key;
        }
      }

      // @ts-expect-error FIXME(strictNullChecks)
      return getLastKey();
    }

    function getKeyForSearch(search: string, fromKey?: Key) {
      // @ts-expect-error FIXME(strictNullChecks)
      let key = fromKey || getFirstKey();
      while (key != null) {
        const item = state.collection.getItem(key);
        // @ts-expect-error FIXME(strictNullChecks)
        const substring = item.textValue.slice(0, search.length);
        // @ts-expect-error FIXME(strictNullChecks)
        if (item.textValue && collator.compare(substring, search) === 0) {
          return key;
        }
        // @ts-expect-error FIXME(strictNullChecks)
        key = getKeyBelow(key);
      }
      return null;
    }

    return {
      getFirstKey,
      getLastKey,
      getKeyAbove,
      getKeyBelow,
      getKeyPageAbove,
      getKeyPageBelow,
      getKeyForSearch,
    };
  }, [
    collator,
    layoutInfo,
    state.collection,
    state.disabledKeys,
    visibleRect.height,
    totalSize,
  ]);

  /**
   * Drop target delegate for this list layout, which calculates
   * the current drop target based upon the layout configuration.
   * Based upon: https://github.com/adobe/react-spectrum/blob/109ea066ed6d953abd2ee2cd7f8051823ae7cf52/packages/%40react-aria/dnd/src/ListDropTargetDelegate.ts#L31
   */

  const dropTargetDelegate = useMemo(() => {
    return {
      getDropTargetFromPoint: (
        __xProp: number, // eventually support x coordiates
        yProp: number,
        isValidDropTarget: (target: DropTarget) => boolean
      ): DropTarget => {
        const y = yProp + visibleRect.y;

        // @ts-expect-error FIXME(strictNullChecks)
        let keyAtPoint: Key = null;

        const rectWithGap = (layout: LayoutInfo) => {
          if (layout.type === 'item') {
            return {
              ...layout.rect,
              height: (layout.gapEnd ?? 0) + layout.rect.height,
            };
          }
          return layout.rect;
        };

        // eslint-disable-next-line no-restricted-syntax
        for (const [, layout] of layoutInfo) {
          if (
            layout.type === 'item' &&
            intersects(rectWithGap(layout), {
              y,
              height: 0,
              x: 0,
              width: 0,
            })
          ) {
            keyAtPoint = layout.key;
            break;
          }
        }

        if (!keyAtPoint) {
          return {
            type: 'root',
          };
        }

        const layout = layoutInfo.get(keyAtPoint);
        // @ts-expect-error FIXME(strictNullChecks)
        const rect = rectWithGap(layout);
        const target: DropTarget = {
          type: 'item',
          // @ts-expect-error FIXME(strictNullChecks)
          key: layout.key,
          dropPosition: 'on',
        };

        if (!isValidDropTarget(target)) {
          if (
            y <= rect.y + rect.height / 2 &&
            isValidDropTarget({ ...target, dropPosition: 'before' })
          ) {
            target.dropPosition = 'before';
          } else if (isValidDropTarget({ ...target, dropPosition: 'after' })) {
            target.dropPosition = 'after';
          }
        } else if (
          y <= rect.y + 10 &&
          isValidDropTarget({ ...target, dropPosition: 'before' })
        ) {
          target.dropPosition = 'before';
        } else if (
          y >= rect.y + rect.height - 10 &&
          isValidDropTarget({ ...target, dropPosition: 'after' })
        ) {
          target.dropPosition = 'after';
        }

        return target;
      },
    };
  }, [layoutInfo, visibleRect.y]);

  return {
    layout,
    keyboardDelegate,
    dropTargetDelegate,
    totalSize,
    layoutInfo,
  };
}
